import { useTranslation } from "react-i18next";
import type { SocialPostListItemDto } from "@multi-chat/shared";
import { Image as ImageIcon } from "lucide-react";
import { ChannelIdentityBadge } from "../../../components/channel/ChannelIdentityBadge.js";
import { LtrValue } from "../../../components/common/LtrValue.js";
import { cn } from "../../../lib/utils.js";
import { formatConversationFullDate, formatConversationListTime } from "../conversation-time.js";

export function PostGroupRowItem({
  item,
  selected,
  nowMs,
  onSelect,
}: {
  item: SocialPostListItemDto;
  selected: boolean;
  nowMs: number;
  onSelect: (postId: string) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const activityAt = item.lastCommentAt ?? item.publishedAt;
  const time = activityAt
    ? formatConversationListTime(activityAt, i18n.language, nowMs, t)
    : "";
  const fullTime = activityAt ? formatConversationFullDate(activityAt, i18n.language) : "";
  const caption = item.caption?.trim() || t("inbox.comments.post.noCaption");

  return (
    <button
      type="button"
      data-post-id={item.id}
      className={cn(
        "relative isolate mx-2 my-0.5 flex min-h-16 w-[calc(100%-1rem)] cursor-pointer items-center gap-2.5 rounded-xl px-2.5 py-[9px] text-start text-inherit transition-transform duration-200 ease-inbox motion-reduce:transition-none",
        "before:absolute before:inset-0 before:-z-10 before:rounded-[inherit] before:border before:border-inbox-line before:bg-surface before:opacity-0 before:shadow-inbox-float before:transition-opacity before:duration-200",
        "hover:-translate-y-px hover:before:opacity-100 motion-reduce:hover:translate-y-0",
        "focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-primary",
        selected &&
          "-translate-y-px before:border-[rgb(23_126_103/0.24)] before:opacity-100 before:shadow-inbox-selected after:absolute after:start-0 after:top-[18px] after:bottom-[18px] after:w-[3px] after:rounded-e-[3px] after:bg-primary motion-reduce:translate-y-0",
      )}
      onClick={() => onSelect(item.id)}
    >
      <span className="relative size-11 shrink-0 overflow-hidden rounded-lg bg-inbox-line-soft">
        {item.mediaPreviewUrl ? (
          <img
            src={item.mediaPreviewUrl}
            alt=""
            className="size-full object-cover"
            draggable={false}
          />
        ) : (
          <span className="grid size-full place-items-center text-inbox-muted">
            <ImageIcon size={18} aria-hidden="true" />
          </span>
        )}
        <span className="absolute bottom-0.5 end-0.5">
          <ChannelIdentityBadge platformType={item.platformType} size="inline" />
        </span>
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <span className="flex min-w-0 items-center gap-1.5">
          <span
            className="min-w-0 flex-1 truncate text-[0.84375rem] font-[530] leading-tight text-inbox-strong"
            dir="auto"
          >
            {caption}
          </span>
          {time ? (
            <time
              className="shrink-0 text-[0.6875rem] tabular-nums text-inbox-subtle"
              dateTime={activityAt ?? undefined}
              title={fullTime || undefined}
            >
              <LtrValue>{time}</LtrValue>
            </time>
          ) : null}
        </span>
        <span className="flex min-w-0 items-center gap-2 text-[0.78125rem] text-inbox-muted">
          <span>
            {t("inbox.comments.post.openCount", {
              open: item.openCommentsCount,
              total: item.totalCommentsCount,
            })}
          </span>
          {item.accountDisplayName || item.accountUsername ? (
            <span className="min-w-0 truncate text-inbox-subtle" dir="auto">
              {item.accountDisplayName?.trim() ||
                (item.accountUsername ? `@${item.accountUsername}` : null)}
            </span>
          ) : null}
        </span>
      </span>
    </button>
  );
}
